import {
  isSearchSourceType,
  type SearchSourceType,
} from '../../../../modules/docs-search/services/search-source';
import type {
  DocsPdfPagePayload,
  DocsSearchNavigablePayload,
} from '../../../../modules/docs-search/services/source-navigation';

export interface AiDocSourceNavigationInput {
  citationId?: string;
  docId: string;
  blockId?: string;
  sourceKind?: 'doc' | 'block' | 'pdf-page' | 'image';
  sourceType?: string;
  elementId?: string;
  parentBlockId?: string;
  parentFlavour?: string;
  noteBlockId?: string;
  displayMode?: string;
  docTitle?: string;
  attachmentName?: string;
  blobKey?: string;
  databaseId?: string;
  databaseRowId?: string;
  databaseColumnId?: string;
  databaseTitle?: string;
  databaseRowLabel?: string;
  databaseColumnName?: string;
  visibility?: 'semantic' | 'technical';
  technicalSourceKind?: string;
  pageIndex?: number;
  query?: string;
  snippet?: string;
  title?: string;
  rects?: DocsPdfPagePayload['rects'];
}

export interface AiDocSourceNavigationDetail {
  kind: 'doc-source';
  source: DocsSearchNavigablePayload;
  requestId: string;
  label?: string;
}

export interface AiMailSourceNavigationDetail {
  kind: 'mail-source';
  messageId: string;
  subject?: string;
  from?: string;
  receivedAt?: string;
  snippet?: string;
  requestId: string;
}

export interface AiCanvasAffectedRefNavigationDetail {
  kind: 'canvas-affected-ref';
  docId: string;
  blockIds?: string[];
  elementIds?: string[];
  requestId: string;
}

export type AiSourceNavigationDetail =
  | AiDocSourceNavigationDetail
  | AiMailSourceNavigationDetail
  | AiCanvasAffectedRefNavigationDetail;

export function toDocsSearchNavigablePayload(
  result: AiDocSourceNavigationInput,
  fallbackQuery: string
): DocsSearchNavigablePayload {
  const sourceType = normalizeSearchSourceType(result.sourceType);
  const title = result.title || result.attachmentName || result.docTitle;
  const blockContent = result.snippet || title || '';

  if (isPdfPageSource(result)) {
    return {
      kind: 'pdf-page',
      docId: result.docId,
      attachmentBlockId: result.blockId,
      pageIndex: result.pageIndex,
      query: result.query || fallbackQuery,
      blockContent,
      rects: result.rects ?? [],
    };
  }

  if (!result.blockId) {
    return {
      docId: result.docId,
      title,
    };
  }

  return {
    docId: result.docId,
    title,
    blockId: result.blockId,
    blockContent,
    ...(sourceType ? { sourceType } : {}),
    ...(result.elementId ? { elementId: result.elementId } : {}),
    ...(result.parentBlockId ? { parentBlockId: result.parentBlockId } : {}),
    ...(result.parentFlavour ? { parentFlavour: result.parentFlavour } : {}),
    ...(result.noteBlockId ? { noteBlockId: result.noteBlockId } : {}),
    ...(result.displayMode ? { displayMode: result.displayMode } : {}),
    ...(result.databaseId ? { databaseId: result.databaseId } : {}),
    ...(result.databaseRowId ? { databaseRowId: result.databaseRowId } : {}),
    ...(result.databaseColumnId
      ? { databaseColumnId: result.databaseColumnId }
      : {}),
    ...(result.visibility ? { visibility: result.visibility } : {}),
    ...(result.technicalSourceKind === 'mail_storage'
      ? { technicalSourceKind: result.technicalSourceKind }
      : {}),
  };
}

export function toAiDocSourceNavigationDetail(
  result: AiDocSourceNavigationInput,
  fallbackQuery: string,
  requestId: string
): AiDocSourceNavigationDetail {
  return {
    kind: 'doc-source',
    source: toDocsSearchNavigablePayload(result, fallbackQuery),
    requestId,
    label: result.title || result.attachmentName || result.docTitle,
  };
}

function normalizeSearchSourceType(
  sourceType: string | undefined
): SearchSourceType | undefined {
  return isSearchSourceType(sourceType) ? sourceType : undefined;
}

function isPdfPageSource(
  result: AiDocSourceNavigationInput
): result is AiDocSourceNavigationInput & {
  blockId: string;
  pageIndex: number;
} {
  return (
    !!result.blockId &&
    Number.isInteger(result.pageIndex) &&
    (result.sourceKind === 'pdf-page' || result.pageIndex !== undefined)
  );
}
